(ns arthur.domain.geom
  "2D similarity transforms and temporal smoothing.

  A transform is {:s :theta :tx :ty}; a point is {:x :y}. Both stay maps at this
  layer: this is the numeric oracle the JS is diffed against, and a faithful
  port is worth more here than a fast one. The dense typed-array
  representations appear at the freeze boundary, not below it.")

(defn centroid
  "Mean of a point set."
  [pts]
  (let [n (count pts)]
    {:x (/ (transduce (map :x) + 0.0 pts) n)
     :y (/ (transduce (map :y) + 0.0 pts) n)}))

(defn fit-similarity
  "Least-squares similarity (translation + rotation + uniform scale, 4 DOF)
  mapping P onto Q. Closed form; no iteration.

  Deliberately NOT affine or homography: the extra degrees of freedom absorb
  out-of-plane head rotation as shear/perspective and smear it into the mouth.
  Four DOF removes exactly translation, roll and depth-scale, and leaves yaw and
  pitch as a measurable residual."
  [P Q]
  (let [n  (count P)
        cp (centroid P)
        cq (centroid Q)
        ;; Dot, cross and squared norm of the centred configurations, in one
        ;; pass. Reduced in input order, so the floating-point result is bit for
        ;; bit what an index loop would give and the 1e-9 parity against the JS
        ;; holds.
        [a b norm]
        (reduce (fn [[a b norm] [p q]]
                  (let [px (- (:x p) (:x cp)) py (- (:y p) (:y cp))
                        qx (- (:x q) (:x cq)) qy (- (:y q) (:y cq))]
                    [(+ a (+ (* px qx) (* py qy)))      ; dot
                     (+ b (- (* px qy) (* py qx)))      ; cross
                     (+ norm (+ (* px px) (* py py)))]))
                [0.0 0.0 0.0]
                (map vector P Q))
        pcx   (:x cp) pcy (:y cp)
        qcx   (:x cq) qcy (:y cq)
        theta (js/Math.atan2 b a)
        ;; A degenerate configuration has nothing to recover a scale from. Fall
        ;; back to 1 rather than dividing by zero: one bad detection frame would
        ;; otherwise poison the Procrustes mean and therefore every frame.
        s     (if (> norm 1e-12) (/ (js/Math.hypot a b) norm) 1)
        c     (js/Math.cos theta)
        sn    (js/Math.sin theta)]
    {:s     s
     :theta theta
     :tx    (- qcx (* s (- (* c pcx) (* sn pcy))))
     :ty    (- qcy (* s (+ (* sn pcx) (* c pcy))))}))

(defn apply-sim [tf p]
  (let [c  (js/Math.cos (:theta tf))
        sn (js/Math.sin (:theta tf))]
    {:x (+ (* (:s tf) (- (* c (:x p)) (* sn (:y p)))) (:tx tf))
     :y (+ (* (:s tf) (+ (* sn (:x p)) (* c (:y p)))) (:ty tf))}))

(defn apply-sim-all [tf pts]
  (mapv #(apply-sim tf %) pts))

(defn fit-residual
  "Residual RMS after the fit, in the units of Q. Rises with out-of-plane
  rotation, so it is the signal for \"this section is not stabilisable\"."
  [tf P Q]
  (let [sq (fn [d] (* d d))]
    (js/Math.sqrt
     (/ (transduce (map (fn [[p q]]
                          (let [m (apply-sim tf p)]
                            (+ (sq (- (:x m) (:x q)))
                               (sq (- (:y m) (:y q)))))))
                   + 0.0 (map vector P Q))
        (count P)))))

(defn procrustes-mean
  "Generalised Procrustes: the reference is the MEAN rigid configuration over the
  shot, not frame zero, so no single frame's idiosyncrasies get baked into every
  other frame. Three passes is plenty."
  ([frames-rigid] (procrustes-mean frames-rigid 3))
  ([frames-rigid iters]
   (let [n     (count frames-rigid)
         ;; One pass: fit every frame onto the current reference, sum the
         ;; aligned configurations, divide. Iterative refinement, so the whole
         ;; thing is `iterate` taken `iters` deep — which is what the algorithm
         ;; actually says, rather than a counter that happens to stop.
         refine (fn [ref]
                  (->> frames-rigid
                       (reduce (fn [acc rig]
                                 (let [moved (apply-sim-all (fit-similarity rig ref) rig)]
                                   (mapv (fn [a m] {:x (+ (:x a) (:x m))
                                                    :y (+ (:y a) (:y m))})
                                         acc moved)))
                               (mapv (constantly {:x 0.0 :y 0.0}) ref))
                       (mapv (fn [p] {:x (/ (:x p) n) :y (/ (:y p) n)}))))]
     (-> (iterate refine (mapv (fn [p] {:x (:x p) :y (:y p)}) (first frames-rigid)))
         (nth iters)))))

(defn moving-average
  "`radius` is in frames either side: 0 is off, 1 averages over 3 frames, 2 over
  5. Expressed as a radius rather than a window so that \"off\" is 0 and every
  value is symmetric - an even window would be lopsided in time."
  [vals radius]
  (if (<= radius 0)
    (vec vals)
    (let [v    (vec vals)
          n    (count v)
          half (js/Math.floor radius)]
      (mapv (fn [i]
              ;; Clamped at the ends rather than shortened, so every output is an
              ;; average of the same COUNT of samples and the first frame is not
              ;; noisier than the rest.
              (let [lo (- i half) hi (+ i half)]
                (/ (reduce + (map (fn [j] (nth v (min (dec n) (max 0 j))))
                                  (range lo (inc hi))))
                   (inc (- hi lo)))))
            (range n)))))

(defn smooth-transforms
  "Smooth the four transform parameters, NEVER the contour. Landmark jitter of a
  pixel is smeared into the mouth by the inverse transform, so the transform is
  where the low-pass belongs; smoothing the contour would destroy the
  performance, which is the entire asset.
  Angles are smoothed as (cos, sin) so wrapping cannot produce a spike."
  [tfs radius]
  (let [c  (moving-average (map #(js/Math.cos (:theta %)) tfs) radius)
        sn (moving-average (map #(js/Math.sin (:theta %)) tfs) radius)
        s  (moving-average (map :s tfs) radius)
        tx (moving-average (map :tx tfs) radius)
        ty (moving-average (map :ty tfs) radius)]
    (mapv (fn [i] {:theta (js/Math.atan2 (nth sn i) (nth c i))
                   :s     (nth s i)
                   :tx    (nth tx i)
                   :ty    (nth ty i)})
          (range (count tfs)))))
